import React from 'react';
import classes from './styles.module.css';

const ACTION_LABELS = {
    account_create: 'Создание аккаунта',
    account_update: 'Изменение аккаунта',
    account_delete: 'Удаление аккаунта',
    account_login: 'Вход в систему',
    account_logout: 'Выход из системы',
    account_grant_admin: 'Назначение администратором',
    account_revoke_admin: 'Снятие прав администратора',
    account_reset_2fa: 'Сброс 2FA',
    account_password_reset: 'Сброс пароля',
    account_project_add: 'Добавление к проекту',
    account_project_remove: 'Удаление из проекта',
    merchant_create: 'Создание проекта',
    merchant_update: 'Изменение проекта',
    merchant_delete: 'Удаление проекта',
    merchant_block: 'Блокировка проекта',
    merchant_unblock: 'Разблокировка проекта',
    merchant_frod_update: 'Изменение антифрода',
    merchant_widget_update: 'Изменение виджета',
    merchant_user_add: 'Добавление пользователя',
    merchant_user_remove: 'Удаление пользователя',
    terminal_create: 'Создание терминала',
    terminal_update: 'Изменение терминала',
    terminal_delete: 'Удаление терминала',
    terminal_balance_sync: 'Синхронизация баланса',
    terminal_status_change: 'Изменение статуса терминала',
    settings_create: 'Создание настройки',
    settings_update: 'Изменение настройки',
    settings_delete: 'Удаление настройки',
    permission_update: 'Изменение прав',
    transaction_callback_resend: 'Повторная отправка уведомления мерчанту',
    transaction_decline: 'Перевод транзакции в отклонение',
    transaction_payout: 'Перевод транзакции в выплату',
    transaction_amount_change: 'Изменение суммы транзакции',
};

const ENTITY_TYPE_LABELS = {
    account: 'Аккаунт',
    Account: 'Учетная запись',
    merchant: 'Проект',
    Merchant: 'Проект',
    terminal: 'Терминал',
    Terminal: 'Терминал',
    settings: 'Настройка',
    MerchantSettings: 'Настройка MID',
    permission: 'Права',
    AccountAccess: 'Права доступа',
    Transaction: 'Транзакция',
};

const FIELD_LABELS = {
    name: 'Название',
    description: 'Описание',
    url: 'Адрес',
    status: 'Статус',
    inn: 'ИНН',
    kpp: 'КПП',
    ogrn: 'ОГРН',
    enabled: 'Активна',
    priority: 'Приоритет',
    providerUuid: 'UUID провайдера',
    providerName: 'Провайдер',
    terminalId: 'ID терминала',
    isSBP: 'СБП',
    isMP2P: 'Р2Р',
    preAuth: 'Предавторизация',
    partialRefund: 'Частичный возврат',
    partialReversal: 'Частичная отмена',
    recurring: 'Рекуррент',
    payoutBalanceCheck: 'Проверка баланса',
    h2h: 'Человек к человеку',
    fee: 'Комиссии',
    frod: 'Антифрод',
    antifraud: 'Настройки антифрода',
    widget: 'Ключ виджета',
    widgetCallback: 'Адрес колбека виджета',
    balance: 'Баланс',
    username: 'Логин',
    email: 'Email',
    privileges: 'Привилегии',
    firstName: 'Имя',
    lastName: 'Фамилия',
    userId: 'ID пользователя',
    projectUuid: 'UUID проекта',
    projectName: 'Проект',
    returnUrl: 'Адрес возврата',
    webhookUrl: 'Адрес колбека',
    exchangeRate: 'Курс обмена',
    bankAccount: 'Банковский счёт',
    paymentMethod: 'Способ оплаты',
    processingId: 'ID процессинга',
    currencyCode: 'Валюта',
};

const PROJECT_PERMISSIONS = {
    1: 'Отмена операции',
    2: 'Установка лимита',
    4: 'Доступ к статистике',
    8: 'Настройка оповещений',
    16: 'Загрузка реестра',
    32: 'Просмотр реестра',
    64: 'Выгрузка отчёта',
    128: 'Список реестров',
    256: 'Просмотр карт',
    512: 'Активация карт',
    1024: 'Отчёт 1С',
    2048: 'Доступ к платежам',
    4096: 'Доступ к ПСА',
};

const ADMIN_PERMISSIONS = {
    1: 'Просмотр аккаунтов',
    2: 'Создание аккаунтов',
    4: 'Список аккаунтов',
    8: 'Редактирование аккаунтов',
    16: 'Создание проектов',
    32: 'Редактирование проектов',
};

function decodePermissions(mask, permMap) {
    if (mask === null || mask === undefined) return [];
    const num = Number(mask);
    const result = [];
    for (const [bit, label] of Object.entries(permMap)) {
        if (num & Number(bit)) result.push(label);
    }
    return result;
}

function formatPrivilegesChange(oldVal, newVal, entityType) {
    const permMap = entityType === 'Account' ? ADMIN_PERMISSIONS : PROJECT_PERMISSIONS;
    const oldPerms = decodePermissions(oldVal, permMap);
    const newPerms = decodePermissions(newVal, permMap);

    const added = newPerms.filter((p) => !oldPerms.includes(p));
    const removed = oldPerms.filter((p) => !newPerms.includes(p));

    const parts = [];
    if (added.length > 0) parts.push('+ ' + added.join(', '));
    if (removed.length > 0) parts.push('− ' + removed.join(', '));
    return parts.length > 0 ? parts.join('; ') : '(без изменений)';
}

function formatFieldValue(value) {
    if (value === null || value === undefined) return '(пусто)';
    if (value === true) return 'включено';
    if (value === false) return 'выключено';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}

const ACTION_COLORS = {
    create: '#4caf50',
    update: '#2196f3',
    delete: '#f44336',
    login: '#9c27b0',
    logout: '#607d8b',
    block: '#ff9800',
    unblock: '#4caf50',
    grant: '#4caf50',
    revoke: '#ff9800',
    reset: '#ff9800',
    sync: '#00bcd4',
    add: '#4caf50',
    remove: '#f44336',
};

function getActionColor(action) {
    if (!action) return '#9e9e9e';

    const actionLower = action.toLowerCase();
    for (const [key, color] of Object.entries(ACTION_COLORS)) {
        if (actionLower.includes(key)) {
            return color;
        }
    }
    return '#9e9e9e';
}

function formatDate(dateString) {
    if (!dateString) return '—';

    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day}.${month}.${year} ${hours}:${minutes}`;
}

function formatChanges(oldData, newData) {
    if (!oldData && !newData) return null;

    const changes = [];
    const allKeys = new Set([...(oldData ? Object.keys(oldData) : []), ...(newData ? Object.keys(newData) : [])]);

    for (const key of allKeys) {
        const oldVal = oldData ? oldData[key] : undefined;
        const newVal = newData ? newData[key] : undefined;

        if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
            changes.push({
                field: key,
                old: oldVal,
                new: newVal,
            });
        }
    }

    return changes.length > 0 ? changes : null;
}

const AuditLogRow = ({ log }) => {
    const [isExpanded, setIsExpanded] = React.useState(false);

    const actionKey = log.action ? log.action.toLowerCase() : '';
    const actionLabel = ACTION_LABELS[actionKey] || log.action || '—';
    const entityTypeLabel = ENTITY_TYPE_LABELS[log.entityType] || log.entityType || '—';
    const actionColor = getActionColor(log.action);
    const changes = formatChanges(log.oldData, log.newData);

    return (
        <React.Fragment>
            <tr
                className={classes.Row}
                onClick={() => changes && setIsExpanded(!isExpanded)}
                style={{ cursor: changes ? 'pointer' : 'default' }}
            >
                <td className={classes.Cell}>
                    <span className={classes.DateTime}>{formatDate(log.createdAt)}</span>
                </td>
                <td className={classes.Cell}>
                    <span className={classes.Actor}>{log.actorUsername || `ID: ${log.actorId}`}</span>
                </td>
                <td className={classes.Cell}>
                    <span className={classes.ActionBadge} style={{ backgroundColor: actionColor }}>
                        {actionLabel}
                    </span>
                </td>
                <td className={classes.Cell}>
                    <span className={classes.EntityType}>{entityTypeLabel}</span>
                </td>
                <td className={classes.Cell}>
                    <span className={classes.EntityName}>
                        {log.entityName || log.entityUuid || log.entityId || '—'}
                    </span>
                </td>
                <td className={classes.CellCenter}>
                    {changes && <span className={classes.ExpandIcon}>{isExpanded ? '▼' : '▶'}</span>}
                </td>
            </tr>
            {isExpanded && changes && (
                <tr className={classes.DetailsRow}>
                    <td colSpan="6" className={classes.DetailsCell}>
                        <div className={classes.Changes}>
                            <div className={classes.ChangesHeader}>Изменения:</div>
                            {changes.map((change, idx) => (
                                <div key={idx} className={classes.ChangeItem}>
                                    {change.field === 'privileges' ? (
                                        <React.Fragment>
                                            <span className={classes.ChangeField}>Права:</span>
                                            <span className={classes.ChangeNew}>
                                                {formatPrivilegesChange(change.old, change.new, log.entityType)}
                                            </span>
                                        </React.Fragment>
                                    ) : (
                                        <React.Fragment>
                                            <span className={classes.ChangeField}>{FIELD_LABELS[change.field] || change.field}:</span>
                                            <span className={classes.ChangeOld}>{formatFieldValue(change.old)}</span>
                                            <span className={classes.ChangeArrow}>→</span>
                                            <span className={classes.ChangeNew}>{formatFieldValue(change.new)}</span>
                                        </React.Fragment>
                                    )}
                                </div>
                            ))}
                            {log.ipAddress && <div className={classes.MetaInfo}>IP: {log.ipAddress}</div>}
                        </div>
                    </td>
                </tr>
            )}
        </React.Fragment>
    );
};

const MobileAuditLogCard = ({ log }) => {
    const [isExpanded, setIsExpanded] = React.useState(false);

    const actionKey = log.action ? log.action.toLowerCase() : '';
    const actionLabel = ACTION_LABELS[actionKey] || log.action || '—';
    const entityTypeLabel = ENTITY_TYPE_LABELS[log.entityType] || log.entityType || '—';
    const actionColor = getActionColor(log.action);
    const changes = formatChanges(log.oldData, log.newData);

    return (
        <button
            type="button"
            className={classes.MobileCard}
            onClick={() => changes && setIsExpanded(!isExpanded)}
            style={{ cursor: changes ? 'pointer' : 'default' }}
        >
            <div className={classes.MobileRow}>
                <span className={classes.DateTime}>{formatDate(log.createdAt)}</span>
                <span className={classes.ActionBadge} style={{ backgroundColor: actionColor }}>
                    {actionLabel}
                </span>
            </div>
            <div className={classes.MobileRow}>
                <span className={classes.Actor}>{log.actorUsername || `ID: ${log.actorId}`}</span>
                <span className={classes.EntityType}>{entityTypeLabel}</span>
            </div>
            <div className={classes.MobileEntityRow}>
                <span className={classes.EntityName}>{log.entityName || log.entityUuid || log.entityId || '—'}</span>
                {changes && <span className={classes.ExpandIcon}>{isExpanded ? '▼' : '▶'}</span>}
            </div>

            {isExpanded && changes && (
                <div className={classes.MobileDetails}>
                    <div className={classes.ChangesHeader}>Изменения:</div>
                    {changes.map((change, idx) => (
                        <div key={idx} className={classes.ChangeItem}>
                            {change.field === 'privileges' ? (
                                <React.Fragment>
                                    <span className={classes.ChangeField}>Права:</span>
                                    <span className={classes.ChangeNew}>
                                        {formatPrivilegesChange(change.old, change.new, log.entityType)}
                                    </span>
                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    <span className={classes.ChangeField}>{FIELD_LABELS[change.field] || change.field}:</span>
                                    <span className={classes.ChangeOld}>{formatFieldValue(change.old)}</span>
                                    <span className={classes.ChangeArrow}>→</span>
                                    <span className={classes.ChangeNew}>{formatFieldValue(change.new)}</span>
                                </React.Fragment>
                            )}
                        </div>
                    ))}
                    {log.ipAddress && <div className={classes.MetaInfo}>IP: {log.ipAddress}</div>}
                </div>
            )}
        </button>
    );
};

const AuditLogsList = ({ logs, isLoading }) => {
    if (isLoading) {
        return (
            <div className={classes.Loading}>
                <div className={classes.Spinner}></div>
                <span>Загрузка журнала...</span>
            </div>
        );
    }

    if (!logs || logs.length === 0) {
        return (
            <div className={classes.Empty}>
                <span className={classes.EmptyIcon}>📋</span>
                <span className={classes.EmptyText}>Записи не найдены</span>
            </div>
        );
    }

    return (
        <React.Fragment>
            <div className={classes.TableScroll}>
                <table className={classes.Table}>
                    <thead>
                        <tr>
                            <th className={classes.HeaderCell} style={{ width: '140px' }}>
                                Дата/время
                            </th>
                            <th className={classes.HeaderCell} style={{ width: '140px' }}>
                                Администратор
                            </th>
                            <th className={classes.HeaderCell} style={{ width: '200px' }}>
                                Действие
                            </th>
                            <th className={classes.HeaderCell} style={{ width: '100px' }}>
                                Тип
                            </th>
                            <th className={classes.HeaderCell}>Объект</th>
                            <th className={classes.HeaderCellCenter} style={{ width: '50px' }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {logs.map((log) => (
                            <AuditLogRow key={log.id} log={log} />
                        ))}
                    </tbody>
                </table>
            </div>

            <div className={classes.MobileList}>
                {logs.map((log) => (
                    <MobileAuditLogCard key={log.id} log={log} />
                ))}
            </div>
        </React.Fragment>
    );
};

export default AuditLogsList;



// WEBPACK FOOTER //
// ./src/Components/ComponentsOfAdministrator/AuditLogsList/index.js